import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Nav from "./Nav";
import Footer from "./Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faChartLine, 
  faUsers, 
  faShieldHalved, 
  faGlobe,
  faStar,
  faArrowRight,
  faRocket,
  faBolt,
  faBuilding
} from "@fortawesome/free-solid-svg-icons";

function Brand() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const brandPerks = []; // Legacy data removed for new Bento UI

  return (
    <div className="bg-white min-h-screen">
      <Nav />
      
      {/* Brand Hero - Premium & Impactful */}
      <section className="pt-40 pb-24 px-4 relative overflow-hidden flex flex-col items-center justify-center text-center">
        {/* Soft Corporate Gradients */}
        <div className="absolute top-0 right-1/4 w-[700px] h-[700px] bg-blue-50/40 blur-[130px] rounded-full -z-10 animate-pulse"></div>
        <div className="absolute bottom-0 left-1/4 w-[700px] h-[700px] bg-cyan-50/40 blur-[130px] rounded-full -z-10"></div>

        <div className="z-20 max-w-5xl">
          <div className="inline-flex items-center gap-2 px-5 py-1.5 bg-blue-50 text-blue-700 border border-blue-100 rounded-full text-[10px] font-bold uppercase tracking-widest mb-10 shadow-sm">
             <FontAwesomeIcon icon={faBuilding} className="text-blue-500" /> Enterprise-Grade Platform
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-gray-900 mb-10 leading-[1.05] tracking-tighter">
            SCALE YOUR <br className="hidden lg:block" />
            <span className="inline-block px-10 py-1.5 bg-transparent text-blue-600 border-[3px] border-blue-600 rounded-full mx-2 transform rotate-1 hover:rotate-0 transition-transform cursor-default">
              INFLUENCE
            </span> <br className="hidden lg:block" />
            WITH PRECISION
          </h1>
          
          <p className="mb-12 text-lg md:text-xl text-gray-500 max-w-2xl mx-auto font-medium px-4 opacity-70 leading-relaxed">
            The infrastructure you need to launch, manage, and scale <br className="hidden md:block" /> world-class creator campaigns with guaranteed ROI.
          </p>

          <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
            <Link 
              to="/onboarding/brand"
              className="inline-flex items-center gap-3 px-12 py-6 bg-black text-white rounded-full text-lg font-black shadow-2xl transition-all hover:scale-105 active:scale-95 hover:shadow-black/20"
            >
              Get Started <FontAwesomeIcon icon={faArrowRight} className="text-base" />
            </Link>
            <button className="text-gray-900 font-bold text-lg hover:opacity-60 transition-opacity">
              Book a Demo
            </button>
          </div>
        </div>
      </section>

      <section className="py-40 px-6 bg-[#f8fafc] relative overflow-hidden">
        {/* Soft Corporate Background Accents */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-50/50 blur-[130px] rounded-full -z-10 -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-cyan-50/50 blur-[130px] rounded-full -z-10 translate-x-1/2 translate-y-1/2"></div>
        <div className="container mx-auto max-w-7xl">
          <div className="mb-20 md:mb-24 flex flex-col lg:flex-row justify-between items-end gap-10">
            <div className="max-w-2xl text-left">
              <h2 className="text-4xl md:text-6xl font-black text-gray-950 mb-6 md:mb-8 tracking-tighter leading-none">
                Engineered for <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 italic">High-Performance</span> Brands.
              </h2>
              <p className="text-lg md:text-xl text-gray-500 font-medium opacity-80 leading-relaxed">
                Stop guessing. Start growing with data-driven creator marketing strategies.
              </p>
            </div>
            <div className="hidden lg:block">
              <div className="w-24 h-24 border-2 border-dashed border-gray-200 rounded-full flex items-center justify-center animate-spin-slow">
                <FontAwesomeIcon icon={faStar} className="text-gray-200 text-3xl" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 lg:grid-cols-12 gap-6 auto-rows-auto md:auto-rows-[300px]">
            {/* Box 1: Global Reach (Blue) - Large Feature */}
            <div className="md:col-span-3 lg:col-span-7 md:row-span-2 group relative bg-[#4A90E2] rounded-[2.5rem] md:rounded-[3.5rem] p-8 md:p-12 overflow-hidden flex flex-col justify-end transition-all duration-500 hover:scale-[1.01] hover:shadow-2xl min-h-[400px] md:min-h-0">
              <div className="absolute top-8 right-8 md:top-12 md:right-12 flex gap-3 md:gap-4 z-10">
                 <div className="w-12 h-12 md:w-16 md:h-16 bg-white/20 backdrop-blur-md rounded-xl md:rounded-2xl flex items-center justify-center text-white text-2xl md:text-3xl animate-float-slow transition-transform hover:scale-110"><FontAwesomeIcon icon={faGlobe} /></div>
                 <div className="w-10 h-10 md:w-12 md:h-12 bg-white/10 backdrop-blur-md rounded-lg md:rounded-xl flex items-center justify-center text-white text-lg md:text-xl animate-float-mid"><FontAwesomeIcon icon={faBuilding} /></div>
              </div>
              
              <div className="absolute inset-0 flex items-center justify-center opacity-10 md:opacity-20 pointer-events-none">
                <div className="w-[150%] aspect-square border-[1px] border-white/30 rounded-full animate-spin-slow"></div>
                <div className="absolute w-[120%] aspect-square border-[1px] border-white/20 rounded-full animate-spin-slow direction-reverse"></div>
              </div>

              <div className="relative z-10 max-w-lg">
                <h3 className="text-3xl md:text-4xl font-black text-white mb-4 md:mb-6 leading-tight tracking-tighter">
                  Scale your brand <br /> across borders.
                </h3>
                <p className="text-white/80 text-sm md:text-base font-bold leading-relaxed">
                  Access a curated network of international creators ready to take your brand to a global audience.
                </p>
              </div>
            </div>

            {/* Box 2: Vetted Creators (Purple) */}
            <div className="md:col-span-2 lg:col-span-5 md:row-span-2 bg-[#8E44AD] rounded-[2.5rem] md:rounded-[3.5rem] p-8 md:p-12 overflow-hidden flex flex-col justify-between transition-all duration-500 hover:scale-[1.01] hover:shadow-2xl min-h-[350px] md:min-h-0">
               <div className="relative h-1/2">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
                  <div className="flex flex-col gap-3 md:gap-4 relative z-10">
                     <div className="bg-white/90 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 shadow-lg flex items-center gap-3 md:gap-4 w-fit animate-float-mid">
                        <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-[10px] md:text-xs shadow-sm"><FontAwesomeIcon icon={faShieldHalved} /></div>
                        <div className="font-black text-gray-800 text-xs md:text-sm">Vetted Profile</div>
                     </div>
                     <div className="bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 shadow-lg flex items-center gap-3 md:gap-4 ml-8 md:ml-12 w-fit animate-float-slow">
                        <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-[10px] md:text-xs shadow-sm"><FontAwesomeIcon icon={faUsers} /></div>
                        <div className="font-black text-gray-800 text-xs md:text-sm">Authentic Reach</div>
                     </div>
                  </div>
               </div>
               <div className="mt-8 md:mt-0">
                  <h3 className="text-2xl md:text-3xl font-black text-white mb-4 md:mb-6 leading-tight tracking-tight">
                    Every creator is <br /> manually verified.
                  </h3>
                  <p className="text-white/80 text-sm md:text-base font-bold leading-relaxed">
                    We ensure style, engagement, and brand-safety for every single campaign.
                  </p>
               </div>
            </div>

            {/* Box 3: Deep Analytics (Emerald) */}
            <div className="md:col-span-2 lg:col-span-5 md:row-span-2 bg-[#27AE60] rounded-[2.5rem] md:rounded-[3.5rem] p-8 md:p-12 overflow-hidden flex flex-col transition-all duration-500 hover:scale-[1.01] hover:shadow-2xl min-h-[400px] md:min-h-0">
               <h3 className="text-2xl md:text-3xl font-black text-white mb-4 md:mb-6 leading-tight tracking-tight">
                  Real-time ROI <br /> tracking.
               </h3>
               <p className="text-white/80 text-sm md:text-base font-bold leading-relaxed mb-8 md:mb-12">
                  Deep sentiment and conversion metrics delivered directly to your enterprise dashboard.
               </p>
               <div className="relative flex-1 min-h-[150px]">
                  <div className="absolute bottom-0 right-0 w-full bg-white/10 p-5 md:p-6 rounded-[2rem] md:rounded-[2.5rem] backdrop-blur-md border border-white/20 shadow-2xl group-hover:-translate-y-4 transition-transform">
                     <div className="flex justify-between items-end mb-3 md:mb-4">
                        <div className="text-white/50 text-[9px] md:text-[10px] font-black uppercase tracking-widest">Sentiment Score</div>
                        <div className="text-xl md:text-2xl font-black text-white">98%</div>
                     </div>
                     <div className="h-1.5 md:h-2 w-full bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full w-full bg-white/40 animate-pulse"></div>
                     </div>
                  </div>
                  <div className="absolute top-0 left-0 w-2/3 bg-white/20 p-5 md:p-6 rounded-[2rem] md:rounded-[2.5rem] backdrop-blur-md border border-white/20 shadow-2xl group-hover:-rotate-3 transition-transform">
                     <FontAwesomeIcon icon={faChartLine} className="text-white text-2xl md:text-3xl mb-3 md:mb-4" />
                     <div className="text-base md:text-lg font-black text-white">4.8x ROI</div>
                  </div>
               </div>
            </div>

            {/* Box 4: Instant Scaling (Orange) */}
            <div className="md:col-span-3 lg:col-span-7 md:row-span-2 bg-[#E67E22] rounded-[2.5rem] md:rounded-[3.5rem] p-8 md:p-12 overflow-hidden flex flex-col justify-end transition-all duration-500 hover:scale-[1.01] hover:shadow-2xl relative min-h-[400px] md:min-h-0">
               <div className="absolute top-12 left-1/2 -translate-x-1/2 flex items-center gap-8 md:gap-12 z-10 w-full justify-center">
                  <div className="relative group/btn">
                    <div className="absolute -inset-4 bg-white/20 blur-xl rounded-full scale-150 animate-pulse"></div>
                    <div className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-full flex items-center justify-center text-orange-600 text-3xl md:text-4xl shadow-2xl relative z-10 transition-transform group-hover/btn:scale-110"><FontAwesomeIcon icon={faRocket} /></div>
                  </div>
                  <div className="hidden sm:flex flex-col gap-2">
                     <div className="h-1.5 w-12 bg-white/20 rounded-full"></div>
                     <div className="h-1.5 w-24 bg-white/40 rounded-full animate-float-mid"></div>
                     <div className="h-1.5 w-16 bg-white/20 rounded-full translate-x-4"></div>
                  </div>
               </div>

               <div className="pt-24 md:pt-24 max-w-xl">
                 <h3 className="text-3xl md:text-4xl font-black text-white mb-4 md:mb-6 leading-tight tracking-tighter">
                    Go from one creator <br /> to thousands in clicks.
                 </h3>
                 <p className="text-white/80 text-sm md:text-base font-bold leading-relaxed">
                    Launch world-class campaigns at scale with our automated, precision-engineered workflows.
                 </p>
               </div>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes float-slow {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-15px); }
          }
          @keyframes float-mid {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          .animate-float-slow { animation: float-slow 5s ease-in-out infinite; }
          .animate-float-mid { animation: float-mid 4s ease-in-out infinite; }
          .animate-spin-slow { animation: spin 20s linear infinite; }
          .direction-reverse { animation-direction: reverse; }
        `}</style>
      </section>

      <section className="py-24 md:py-48 px-4 md:px-6 bg-white relative overflow-hidden">
        {/* Subtle Background Accents */}
        <div className="absolute top-0 right-0 w-[400px] md:w-[800px] h-[400px] md:h-[800px] bg-blue-50/40 blur-[100px] md:blur-[150px] rounded-full translate-x-1/4 -translate-y-1/4 -z-10"></div>
        <div className="absolute bottom-0 left-0 w-[400px] md:w-[800px] h-[400px] md:h-[800px] bg-indigo-50/40 blur-[100px] md:blur-[150px] rounded-full -translate-x-1/4 translate-y-1/4 -z-10"></div>

        <div className="container mx-auto max-w-7xl flex flex-col lg:flex-row items-center gap-16 lg:gap-32 relative z-10">
          <div className="lg:w-2/5 text-left w-full">
            <h2 className="text-4xl md:text-7xl font-black mb-8 md:mb-12 leading-[0.95] tracking-tighter text-gray-950">
              Live <br /> Performance <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 drop-shadow-sm">Metrics.</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-500 font-medium mb-12 md:mb-20 opacity-70 leading-relaxed max-w-md">
              Real data from thousands of live campaigns. See the impact we deliver every single minute.
            </p>
            
            <Link 
              to="/onboarding/brand"
              className="group inline-flex items-center gap-3 md:gap-4 text-xl md:text-2xl font-black text-gray-950 transition-all"
            >
              <span className="relative">
                Start Your Campaign
                <div className="absolute -bottom-1 md:-bottom-2 left-0 w-full h-0.5 md:h-1 bg-blue-600 transform origin-left transition-transform group-hover:scale-x-110"></div>
              </span>
              <FontAwesomeIcon icon={faArrowRight} className="group-hover:translate-x-3 transition-transform text-lg md:text-xl" />
            </Link>
          </div>

          <div className="lg:w-3/5 w-full mt-12 lg:mt-0">
            <div className="relative group">
              {/* Premium Performance Container */}
              <div className="bg-white/80 border border-gray-100/50 rounded-[2.5rem] md:rounded-[4.5rem] p-6 md:p-14 shadow-[0_50px_100px_rgba(0,0,0,0.08)] backdrop-blur-3xl overflow-hidden relative">
                <div className="flex justify-between items-center mb-8 md:mb-16 px-2">
                  <div>
                    <div className="text-[9px] md:text-[10px] font-black tracking-[0.3em] uppercase text-blue-600 mb-2">Network Hub</div>
                    <div className="text-2xl md:text-3xl font-black tracking-tighter text-gray-950">Campaign Flux</div>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="px-3 md:px-5 py-1.5 md:py-2.5 bg-blue-50 text-blue-600 border border-blue-100 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 md:gap-2 shadow-sm transition-all group-hover:scale-105">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-500 rounded-full animate-ping"></div>
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-500 rounded-full absolute"></div>
                      System Optimizing
                    </div>
                  </div>
                </div>

                <div className="space-y-3 md:space-y-4">
                  {[
                    { brand: "Nike", metric: "ROI", value: "4.8x", trend: "+12%", color: "bg-blue-600" },
                    { brand: "Sony", metric: "Reach", value: "2.4M", trend: "+24%", color: "bg-purple-600" },
                    { brand: "Adobe", metric: "Conversions", value: "18.5k", trend: "+8%", color: "bg-emerald-500" },
                    { brand: "Tesla", metric: "Sentiment", value: "98%", trend: "+5%", color: "bg-orange-500" }
                  ].map((stat, i) => (
                    <div key={i} className="flex items-center justify-between p-4 md:p-7 bg-gray-50/40 rounded-[2rem] md:rounded-[2.5rem] border border-gray-100/50 hover:bg-white hover:shadow-xl hover:shadow-gray-200/50 hover:-translate-y-1 transition-all duration-300 group/stat cursor-default">
                      <div className="flex items-center gap-4 md:gap-6">
                        <div className={`w-12 h-12 md:w-16 md:h-16 ${stat.color} rounded-xl md:rounded-2xl flex items-center justify-center font-black text-xl md:text-2xl text-white shadow-lg transform group-hover/stat:scale-110 group-hover/stat:rotate-3 transition-all`}>
                          {stat.brand[0]}
                        </div>
                        <div>
                          <div className="font-black text-lg md:text-2xl text-gray-950 mb-0.5 tracking-tight">{stat.brand}</div>
                          <div className="text-[9px] md:text-[10px] text-gray-400 font-bold uppercase tracking-[0.15em]">{stat.metric} Tracking</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl md:text-3xl font-black text-blue-600 tracking-tighter mb-0.5">{stat.value}</div>
                        <div className="text-[9px] md:text-[10px] text-green-500 font-black tracking-widest">{stat.trend}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Decorative Bottom Glow */}
                <div className="absolute -bottom-20 left-0 w-full h-40 bg-gradient-to-t from-blue-50/50 to-transparent pointer-events-none"></div>
              </div>

              {/* Decorative Accent */}
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-3/4 h-20 bg-blue-600/10 blur-[60px] -z-10 animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - Brand Edition */}
      <section className="py-48 px-4 relative overflow-hidden bg-white text-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-screen h-screen bg-blue-50/30 blur-[200px] rounded-full -z-10"></div>
        
        <div className="container mx-auto max-w-5xl relative z-10">
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-black text-gray-900 mb-16 leading-[0.85] tracking-tighter">
            GROW <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">BEYOND.</span>
          </h2>
          
          <p className="text-xl md:text-2xl text-gray-500 mb-20 font-medium max-w-2xl mx-auto opacity-70 leading-relaxed px-4">
            Join the world's most innovative brands and redefine what's possible with creator marketing.
          </p>
          
          <Link 
            to="/onboarding/brand"
            className="group relative inline-flex items-center gap-6 px-14 py-7 bg-black text-white rounded-full text-2xl font-black shadow-[0_20px_60px_rgba(37,99,235,0.2)] transition-all hover:scale-110 active:scale-95 hover:bg-gray-900"
          >
             LAUNCH CAMPAIGN
             <div className="w-12 h-12 bg-white text-black rounded-full flex items-center justify-center group-hover:rotate-45 transition-transform">
               <FontAwesomeIcon icon={faRocket} className="text-lg" />
             </div>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Brand;
